import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import WebSocketManager from "./websocketManager";
import server from "../utils/serverConfig";
import apiClient from "../utils/apiClient";

// Create context
export const GlobalDataContext = createContext(null);

// API endpoints
const API_ENDPOINTS = {
  USER_MAPPINGS: `${server.godseye.baseUrl}api/multipliers/by-users`, // Unified endpoint
  USERS: `${server.godseye.baseUrl}get-fyers-users`,
  LIVE_ORDERS: `${server.godseye.baseUrl}live_orders_data_direct`, // Fallback only
  LIVE_POSITIONS: `${server.godseye.baseUrl}live_positions_data_direct`,
  WEBSOCKET_URL: "wss://godseye.ws.server.sypto.xyz/ws/frontend_redis", // Updated to your local WebSocket
};

// Batch processing configuration optimized for 10,000+ order_update messages
// OPTIMIZED FOR UI RESPONSIVENESS - minimal delays
const BATCH_CONFIG = {
  MAX_BATCH_SIZE: 100, // Process immediately when queue reaches 100 orders
  BATCH_DELAY_MS: 1, // ULTRA-FAST processing - 1ms delay for UI responsiveness
  SINGLE_ORDER_DELAY_MS: 0, // IMMEDIATE processing for single orders - 0ms delay
  LARGE_BATCH_SIZE: 500, // For very large batches, process immediately
  LARGE_BATCH_DELAY_MS: 1, // Ultra-fast processing for high-volume scenarios
  MASSIVE_BATCH_SIZE: 1000, // For massive batches (10k+ orders)
  MASSIVE_BATCH_DELAY_MS: 0, // INSTANT processing for massive scenarios
  HIGH_FREQUENCY_THRESHOLD: 50, // If 50+ orders arrive in 100ms, enter high-frequency mode
  HIGH_FREQUENCY_BATCH_SIZE: 200, // Larger batches in high-frequency mode for efficiency
  HIGH_FREQUENCY_DELAY_MS: 1, // ULTRA-FAST processing in high-frequency mode for UI
};

// Position batch processing configuration to prevent race conditions
const POSITION_BATCH_CONFIG = {
  MAX_BATCH_SIZE: 50, // Process when queue reaches 50 positions
  BATCH_DELAY_MS: 20, // 20ms delay for batching
  SINGLE_POSITION_DELAY_MS: 5, // 5ms for single position updates
  CLOSED_POSITION_DELAY_MS: 50, // Wait 50ms before processing closed positions to ensure updates are processed first
};

/**
 * Global Data Provider
 * Handles WebSocket for real-time data: positions, orders, trades, LTP updates
 * Optimized for high-frequency order updates (1000+ orders)
 * WebSocket endpoint: ws://localhost:8000/ws/frontend
 */
export function GlobalDataProvider({ children }) {
  // Use refs to store actual data - prevents re-renders on every update
  const positionsRef = useRef([]);
  const ordersRef = useRef([]);

  // Update counters for version tracking
  const positionsUpdateCounter = useRef(0);
  const ordersUpdateCounter = useRef(0);

  // State only for version counters - consumers use these to know when to re-read from refs
  const [positionsUpdateVersion, setPositionsUpdateVersion] = useState(0);
  const [ordersUpdateVersion, setOrdersUpdateVersion] = useState(0);

  // Keep state copies for backward compatibility with components not yet optimized
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [userMappings, setUserMappings] = useState([]);
  const [users, setUsers] = useState([]);
  const [indexMultipliers, setIndexMultipliers] = useState([]); // Transformed format for modals
  const [indexMultipliersRaw, setIndexMultipliersRaw] = useState([]); // Raw format for AccountView

  // High-performance batch processing for 10,000+ order updates
  const orderUpdateQueue = useRef([]);
  const batchProcessTimer = useRef(null);
  const highFrequencyMode = useRef(false);
  const orderTimestamps = useRef([]); // Track order arrival times
  const lastBatchTime = useRef(Date.now());

  // Position batch processing to prevent race conditions
  const positionUpdateQueue = useRef([]);
  const positionBatchTimer = useRef(null);
  const closedPositionQueue = useRef([]);
  const closedPositionTimer = useRef(null);

  // Loading states - only for initial load
  const [loading, setLoading] = useState({
    positions: true,
    orders: true,
    rejectedOrders: true,
    userMappings: true,
    users: true,
    indexMultipliers: true,
  });

  // Track if initial load is complete
  const [initialLoadComplete, setInitialLoadComplete] = useState({
    positions: false,
    orders: false,
    userMappings: false,
    users: false,
    indexMultipliers: false,
  });

  // Error states
  const [errors, setErrors] = useState({});

  // WebSocket reference
  const [ws, setWs] = useState(null);
  // WebSocket connected state
  const [wsConnected, setWsConnected] = useState(false);

  // LTP tracking
  const [currentLTPs, setCurrentLTPs] = useState({});

  // Helper to update positions (both ref and state)
  const updatePositions = useCallback((updaterFn) => {
    // Update ref immediately (no re-render)
    if (typeof updaterFn === "function") {
      positionsRef.current = updaterFn(positionsRef.current);
    } else {
      positionsRef.current = updaterFn;
    }

    // Increment version to notify consumers
    positionsUpdateCounter.current++;
    setPositionsUpdateVersion(positionsUpdateCounter.current);

    // Also update state for backward compatibility (this causes re-render)
    setPositions(positionsRef.current);
  }, []);

  // Helper to update orders (both ref and state)
  const updateOrders = useCallback((updaterFn) => {
    // Update ref immediately (no re-render)
    if (typeof updaterFn === "function") {
      ordersRef.current = updaterFn(ordersRef.current);
    } else {
      ordersRef.current = updaterFn;
    }

    // Increment version to notify consumers
    ordersUpdateCounter.current++;
    setOrdersUpdateVersion(ordersUpdateCounter.current);

    // Also update state for backward compatibility (this causes re-render)
    setOrders(ordersRef.current);
  }, []);

  // Batch process position updates to prevent race conditions
  const processBatchedPositionUpdates = useCallback(() => {
    if (positionUpdateQueue.current.length === 0) return;

    const startTime = performance.now();
    const batchToProcess = [...positionUpdateQueue.current];
    positionUpdateQueue.current = [];

    // console.log(
    //   "🔄 [POSITION BATCH] Processing:",
    //   batchToProcess.length,
    //   "position updates"
    // );

    updatePositions((prev) => {
      const positionMap = new Map();
      prev.forEach((pos) => {
        const key = `${pos.client_id}_${pos.symbol}_${pos.strategy || "default"
          }`;
        positionMap.set(key, pos);
      });

      let updatedCount = 0;
      let addedCount = 0;
      let removedCount = 0;

      batchToProcess.forEach((positionData) => {
        const key = `${positionData.client_id}_${positionData.symbol}_${positionData.strategy || "default"
          }`;

        // Check if position is closed (net_qty = 0)
        const netQty = positionData.net_qty || positionData.qty || 0;
        const isClosed = Math.abs(netQty) === 0;

        if (isClosed) {
          // Remove closed position from map
          if (positionMap.has(key)) {
            console.log("🗑️ [POSITION BATCH] Removing closed position:", {
              client_id: positionData.client_id,
              symbol: positionData.symbol,
              strategy: positionData.strategy,
              net_qty: netQty,
            });
            positionMap.delete(key);
            removedCount++;
          }
        } else if (positionMap.has(key)) {
          // Update existing position
          const existing = positionMap.get(key);
          positionMap.set(key, {
            ...existing,
            ...positionData,
            last_updated: new Date().toISOString(),
          });
          updatedCount++;
        } else {
          // Add new position
          positionMap.set(key, {
            ...positionData,
            last_updated: new Date().toISOString(),
          });
          addedCount++;
        }
      });

      const finalPositions = Array.from(positionMap.values());
      const processingTime = (performance.now() - startTime).toFixed(2);

      console.log("✅ [POSITION BATCH] Completed:", {
        total: finalPositions.length,
        updated: updatedCount,
        added: addedCount,
        removed: removedCount,
        timeMs: processingTime,
      });

      return finalPositions;
    });

    console.log(
      `⏰ Positions version updated to ${positionsUpdateCounter.current}`
    );
  }, [updatePositions]);

  // Queue position update for batch processing
  const queuePositionUpdate = useCallback(
    (positionData) => {
      positionUpdateQueue.current.push(positionData);

      if (positionBatchTimer.current) {
        clearTimeout(positionBatchTimer.current);
      }

      const queueSize = positionUpdateQueue.current.length;

      if (queueSize >= POSITION_BATCH_CONFIG.MAX_BATCH_SIZE) {
        // Process immediately if batch is full
        processBatchedPositionUpdates();
      } else if (queueSize === 1) {
        // Single position - short delay
        positionBatchTimer.current = setTimeout(
          processBatchedPositionUpdates,
          POSITION_BATCH_CONFIG.SINGLE_POSITION_DELAY_MS
        );
      } else {
        // Normal batching delay
        positionBatchTimer.current = setTimeout(
          processBatchedPositionUpdates,
          POSITION_BATCH_CONFIG.BATCH_DELAY_MS
        );
      }
    },
    [processBatchedPositionUpdates]
  );

  // Process closed positions with delay to ensure all updates are processed first
  const processBatchedClosedPositions = useCallback(() => {
    if (closedPositionQueue.current.length === 0) return;

    const startTime = performance.now();
    const batchToProcess = [...closedPositionQueue.current];
    closedPositionQueue.current = [];

    console.log(
      "🗑️ [CLOSE BATCH] Processing:",
      batchToProcess.length,
      "position closures"
    );

    updatePositions((prev) => {
      let finalPositions = [...prev];
      let removedCount = 0;

      batchToProcess.forEach((closedInfo) => {
        const beforeLength = finalPositions.length;

        // Try multiple matching strategies
        finalPositions = finalPositions.filter((position) => {
          // Primary match: client_id + symbol + strategy
          const match1 =
            position.client_id === closedInfo.client_id &&
            position.symbol === closedInfo.symbol &&
            (position.strategy || "default") ===
              (closedInfo.strategy || "default");

          // Secondary match: client_id + symbol (ignore strategy)
          const match2 =
            position.client_id === closedInfo.client_id &&
            position.symbol === closedInfo.symbol;

          // Tertiary match: position_id if available
          const match3 =
            closedInfo.position_id &&
            (position.id === closedInfo.position_id ||
              position.position_id === closedInfo.position_id);

          const shouldRemove = match1 || match2 || match3;

          if (shouldRemove) {
            console.log("🗑️ [CLOSE BATCH] Removing position:", {
              client_id: position.client_id,
              symbol: position.symbol,
              strategy: position.strategy,
              matchType: match1 ? "exact" : match2 ? "partial" : "id",
            });
          }

          return !shouldRemove;
        });

        const removed = beforeLength - finalPositions.length;
        removedCount += removed;

        if (removed === 0) {
          console.warn(
            "⚠️ [CLOSE BATCH] Could not find position to remove:",
            closedInfo
          );
        }
      });

      const processingTime = (performance.now() - startTime).toFixed(2);
      console.log("✅ [CLOSE BATCH] Completed:", {
        processed: batchToProcess.length,
        removed: removedCount,
        remaining: finalPositions.length,
        timeMs: processingTime,
      });

      return finalPositions;
    });
  }, [updatePositions]);

  // Queue closed position with delay to ensure updates process first
  const queueClosedPosition = useCallback(
    (closedInfo) => {
      closedPositionQueue.current.push(closedInfo);

      if (closedPositionTimer.current) {
        clearTimeout(closedPositionTimer.current);
      }

      // Always use a delay for closed positions to ensure all pending updates are processed
      closedPositionTimer.current = setTimeout(
        processBatchedClosedPositions,
        POSITION_BATCH_CONFIG.CLOSED_POSITION_DELAY_MS
      );
    },
    [processBatchedClosedPositions]
  );

  // Debug: Track positions state changes
  useEffect(() => {
    // console.log("🔄 [POSITIONS STATE] Positions state changed:", {
    //   count: positions.length,
    //   timestamp: new Date().toISOString(),
    //   samplePositions: positions.slice(0, 3).map((p) => ({
    //     client_id: p.client_id,
    //     symbol: p.symbol,
    //     strategy: p.strategy,
    //     pl: p.pl,
    //     ltp: p.current_ltp || p.ltp,
    //     last_updated: p.last_updated,
    //   })),
    // });
  }, [positions]);

  // Fetch user mappings (once on mount)
  useEffect(() => {
    fetchUserMappings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch users (once on mount)
  useEffect(() => {
    fetchUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


    useEffect(() => {
    fetchRejectedOrders();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Orders are now handled via WebSocket - no polling needed
  // Remove the orders polling effect  // Setup WebSocket for positions
  // DELAYED to prioritize Account View data (userMappings, users, indexMultipliers)
 // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch user mappings
  const fetchUserMappings = async () => {
    try {
      if (!initialLoadComplete.userMappings) {
        setLoading((prev) => ({
          ...prev,
          userMappings: true,
          indexMultipliers: true,
        }));
      }

      const response = await apiClient.get(API_ENDPOINTS.USER_MAPPINGS);
      const result = response.data;
      console.log(
        "✅ User Mappings API response:",
        result,
        "at",
        new Date().toISOString()
      );

      // Extract data array from response
      const data = result.data || result;
      const mappingsArray = Array.isArray(data) ? data : [];
      console.log("✅ User Mappings processed:", mappingsArray.length, "users");

      setUserMappings(mappingsArray);

      // Also populate indexMultipliers from the same data
      const multipliersArray = [];
      const rawArray = mappingsArray;

      if (Array.isArray(rawArray)) {
        rawArray.forEach((client) => {
          const clientId = client.client_id;
          const clientName = client.name;

          // console.log(
          //   `🔄 Processing client: ${clientName} (${clientId}) - NIFTY multiplier: ${client.multiplier}`
          // );

          // NIFTY uses 'multiplier' field (updated)
          if (client.multiplier > 0) {
            multipliersArray.push({
              client_id: clientId,
              client_name: clientName,
              broker: client.broker, // Added broker
              index: "NIFTY",
              multiplier: client.multiplier,
            });
          }
          if (client.banknifty_multiplier > 0) {
            multipliersArray.push({
              client_id: clientId,
              client_name: clientName,
              broker: client.broker, // Added broker
              index: "BANKNIFTY",
              multiplier: client.banknifty_multiplier,
            });
          }
          if (client.finnifty_multiplier > 0) {
            multipliersArray.push({
              client_id: clientId,
              client_name: clientName,
              broker: client.broker, // Added broker
              index: "FINNIFTY",
              multiplier: client.finnifty_multiplier,
            });
          }
          if (client.midcpnifty_multiplier > 0) {
            multipliersArray.push({
              client_id: clientId,
              client_name: clientName,
              broker: client.broker, // Added broker
              index: "MIDCPNIFTY",
              multiplier: client.midcpnifty_multiplier,
            });
          }
          if (client.sensex_multiplier > 0) {
            multipliersArray.push({
              client_id: clientId,
              client_name: clientName,
              broker: client.broker, // Added broker
              index: "SENSEX",
              multiplier: client.sensex_multiplier,
            });
          }
        });
      }



      // Store both formats
      setIndexMultipliers(multipliersArray);
      setIndexMultipliersRaw(rawArray); // For AccountView table

      setErrors((prev) => ({
        ...prev,
        userMappings: null,
        indexMultipliers: null,
      }));
      setInitialLoadComplete((prev) => ({
        ...prev,
        userMappings: true,
        indexMultipliers: true,
      }));
    } catch (error) {
      console.error("❌ Error fetching user mappings:", error);
      setErrors((prev) => ({
        ...prev,
        userMappings: error.message,
        indexMultipliers: error.message,
      }));
      setUserMappings([]);
      setIndexMultipliers([]);
      setIndexMultipliersRaw([]);
    } finally {
      setLoading((prev) => ({
        ...prev,
        userMappings: false,
        indexMultipliers: false,
      }));
      console.log(
        "🔓 User Mappings and Index Multipliers loading state set to FALSE"
      );
    }
  };

  // Fetch users
  const fetchUsers = async () => {
    try {
      if (!initialLoadComplete.users) {
        setLoading((prev) => ({ ...prev, users: true }));
      }

      const response = await apiClient.get(API_ENDPOINTS.USERS);
      const data = response.data;
 

      const usersArray = Array.isArray(data) ? data : [];
      setUsers(usersArray);


      // console.log("ALL USERS DATA----------", usersArray);
      setErrors((prev) => ({ ...prev, users: null }));
      setInitialLoadComplete((prev) => ({ ...prev, users: true }));
    } catch (error) {
      console.error("❌ Error fetching users:", error);
      setErrors((prev) => ({ ...prev, users: error.message }));
      setUsers([]);
    } finally {
      setLoading((prev) => ({ ...prev, users: false }));

    
    }
  };

  // Fetch orders via WebSocket sync request
  const fetchOrders = async () => {
    try {
    
      if (ws && ws.readyState === WebSocket.OPEN) {
        // Send sync request to WebSocket
        ws.send(JSON.stringify({ type: "request_sync" }));
    
      } else {
        
        // Fallback to API if WebSocket is not available
        const response = await apiClient.get(API_ENDPOINTS.LIVE_ORDERS);
        const rawData = response.data;
        const filteredData = Array.isArray(rawData)
          ? rawData.filter(shouldShowOrder)
          : [];

        updateOrders(filteredData);
        setErrors((prev) => ({ ...prev, orders: null }));
      }
    } catch (error) {
      console.error("❌ Error refreshing orders:", error);
      setErrors((prev) => ({ ...prev, orders: error.message }));
    }
  };


  const [rejectedOrders, setRejectedOrders] = useState([]);
  const fetchRejectedOrders = async () => {
    try {
      setLoading((prev) => ({ ...prev, rejectedOrders: true }));
      setErrors((prev) => ({ ...prev, rejectedOrders: null }));

   

      const res = await apiClient.get(`${server.godseye.baseUrl}rejected_orders_data`);
      const data = res.data;
     
      if (!Array.isArray(data)) {
        setErrors((prev) => ({ ...prev, rejectedOrders: "Invalid response format" }));
        return;
      }

      // 🟢 Step 1: Deduplicate based on ALL 8 fields
      const uniqueMap = new Map();

      data.forEach((order) => {
        const key = [
          order.symbol,
          order.side,
          order.qty,
          order.remainingQuantity,
          order.limitPrice,
          order.stopPrice,
          order.status,
          order.strategy
        ].join("|"); // composite key

        const existing = uniqueMap.get(key);

        // Keep the most recent one
        if (
          !existing ||
          new Date(order.orderDateTime) > new Date(existing.orderDateTime)
        ) {
          uniqueMap.set(key, order);
        }
      });

      const finalFilteredOrders = Array.from(uniqueMap.values());

     
      // 🟢 Step 2: Sort latest orderDateTime → top
      finalFilteredOrders.sort(
        (a, b) => new Date(b.orderDateTime) - new Date(a.orderDateTime)
      );

      setRejectedOrders(finalFilteredOrders);
    } catch (err) {
      console.error("❌ Error fetching rejected orders:", err);
      setErrors((prev) => ({ ...prev, rejectedOrders: err.message }));
      setRejectedOrders([]);
    } finally {
      setLoading((prev) => ({ ...prev, rejectedOrders: false }));
    }
  };



  // Calculate P&L
  const calculatePositionPnL = useCallback((position, currentLtp) => {
    try {
      const side = parseInt(position.side || 1);
      const netAvg = parseFloat(position.net_avg || position.avg_price || 0);
      const netQty = parseFloat(position.net_qty || position.qty || 0);

      if (!currentLtp || currentLtp <= 0 || netAvg <= 0) {
        return 0;
      }

      let pnl = 0;
      if (side === 1) {
        // Long position
        pnl = (currentLtp - netAvg) * netQty;
      } else {
        // Short position
        pnl = (netAvg - currentLtp) * Math.abs(netQty);
      }

      return isFinite(pnl) ? Math.round(pnl * 100) / 100 : 0;
    } catch (error) {
      console.error("P&L calculation error:", error);
      return 0;
    }
  }, []);


  // Helper function to check if an order should be displayed
  const shouldShowOrder = useCallback((order) => {
    const statusNo = parseInt(order.status_no);

    // Based on your status mapping:
    // 1 => Canceled, 2 => Traded/Filled, 3 => (Not used), 4 => Transit,
    // 5 => Rejected, 6 => Pending, 7 => Expired
    // Show both Transit (4) and Pending (6) orders as they are active

    if (statusNo === 6 || statusNo === 4) {
      // This is a pending order - show it
      return true;
    }

    // Log what we're hiding and why (only occasionally to reduce spam)
    if (Math.random() < 0.01) {
      // Only log 1% of filtered orders to reduce spam
      const statusMap = {
        1: "Canceled",
        2: "Traded/Filled",
        3: "Not used",
        4: "Transit",
        5: "Rejected",
        6: "Pending",
        7: "Expired",
      };

      const statusName = statusMap[statusNo] || `Unknown(${statusNo})`;

    }
    return false;
  }, []);

  // Debug function to check current positions in state
  const debugCurrentPositions = useCallback(() => {

    // Group by symbol
    const bySymbol = {};
    positions.forEach((pos) => {
      if (!bySymbol[pos.symbol]) bySymbol[pos.symbol] = [];
      bySymbol[pos.symbol].push(pos);
    });



    // Show recent updates
    const recentUpdates = positions
      .filter((p) => p.last_updated)
      .sort((a, b) => new Date(b.last_updated) - new Date(a.last_updated))
      .slice(0, 5);

    return { total: positions.length, bySymbol, recentUpdates };
  }, [positions]);

  // Debug function to check current orders in state
  const debugCurrentOrders = useCallback(() => {

  }, [orders]);

  // High-performance batch processing for 10,000+ orders
  const processBatchedOrderUpdates = useCallback(() => {
    if (orderUpdateQueue.current.length === 0) return;

    const startTime = performance.now();
    const batchToProcess = [...orderUpdateQueue.current];
    orderUpdateQueue.current = [];

    const batchSize = batchToProcess.length;
    let logLevel = "🔄 Batch";

    if (batchSize >= BATCH_CONFIG.MASSIVE_BATCH_SIZE) {
      logLevel = "🚀 MASSIVE BATCH";
    } else if (batchSize >= BATCH_CONFIG.LARGE_BATCH_SIZE) {
      logLevel = "🔥 LARGE BATCH";
    }

    console.log(`${logLevel} processing:`, batchSize, "orders");

    updateOrders((prevOrders) => {
      // Use Map for O(1) lookups - critical for 10k+ orders
      const orderMap = new Map();
      prevOrders.forEach((order, index) => {
        orderMap.set(order.order_id, { order, index });
      });

      const ordersToUpdate = [];
      const ordersToAdd = [];
      const ordersToRemove = new Set();
      let processedCount = 0;

      // Process all batched updates efficiently
      batchToProcess.forEach((orderUpdate) => {
        processedCount++;

        // Check if this order should be displayed (only pending orders)
        if (!shouldShowOrder(orderUpdate)) {
          // Mark for removal if it exists
          if (orderMap.has(orderUpdate.order_id)) {
            ordersToRemove.add(orderUpdate.order_id);
          }
          return;
        }

        const existing = orderMap.get(orderUpdate.order_id);
        if (existing) {
          // Update existing order
          ordersToUpdate.push({ index: existing.index, order: orderUpdate });
        } else {
          // Add new order
          ordersToAdd.push(orderUpdate);
        }
      });

      // Apply all changes in one operation
      let finalOrders = [...prevOrders];

      // Remove orders (process in reverse to maintain indices)
      const indicesToRemove = [];
      ordersToRemove.forEach((orderId) => {
        const existing = orderMap.get(orderId);
        if (existing) {
          indicesToRemove.push(existing.index);
        }
      });
      indicesToRemove
        .sort((a, b) => b - a)
        .forEach((index) => {
          finalOrders.splice(index, 1);
        });

      // Update existing orders
      ordersToUpdate.forEach(({ index, order }) => {
        if (finalOrders[index]) {
          finalOrders[index] = order;
        }
      });

      // Add new orders
      finalOrders = [...finalOrders, ...ordersToAdd];

      const processingTime = (performance.now() - startTime).toFixed(2);
      const timeSinceLastBatch = Date.now() - lastBatchTime.current;
      lastBatchTime.current = Date.now();

      const stats = {
        total: finalOrders.length,
        processed: processedCount,
        updated: ordersToUpdate.length,
        added: ordersToAdd.length,
        removed: ordersToRemove.size,
        timeMs: processingTime,
        timeSinceLastMs: timeSinceLastBatch,
        mode: highFrequencyMode.current ? "HIGH-FREQ" : "NORMAL",
        ordersPerSecond: Math.round(
          processedCount / (parseFloat(processingTime) / 1000)
        ),
      };

      // console.log(`✅ ${logLevel} completed:`, stats);
      // console.log(
      //   "📋 Orders state updated: previous =",
      //   prevOrders.length,
      //   "→ new =",
      //   finalOrders.length
      // );

      // Log buy orders specifically for debugging Buy Order View
      const buyOrders = finalOrders.filter((o) => o.side === 1);


      return finalOrders;
    });

    console.log(
      `⏰ Orders version updated to ${ordersUpdateCounter.current} - triggering React re-render`
    );
  }, [shouldShowOrder, updateOrders]);

  // Queue order update for batch processing optimized for 10k+ orders
  const queueOrderUpdate = useCallback(
    (orderData) => {
      const now = Date.now();
      orderUpdateQueue.current.push(orderData);
      orderTimestamps.current.push(now);

      // Clean old timestamps (keep only last 200ms for 10k+ scenario)
      orderTimestamps.current = orderTimestamps.current.filter(
        (timestamp) => now - timestamp <= 200
      );

      // Detect high-frequency scenario (more aggressive for 10k+ orders)
      const recentOrderCount = orderTimestamps.current.length;
      const wasHighFreq = highFrequencyMode.current;
      highFrequencyMode.current =
        recentOrderCount >= BATCH_CONFIG.HIGH_FREQUENCY_THRESHOLD;

      if (highFrequencyMode.current && !wasHighFreq) {
        console.log(
          "🚀 High-frequency mode activated:",
          recentOrderCount,
          "orders/200ms"
        );
      }

      // Clear existing timer
      if (batchProcessTimer.current) {
        clearTimeout(batchProcessTimer.current);
      }

      const queueSize = orderUpdateQueue.current.length;
      let maxBatchSize, delay;

      // Choose optimal batch size and delay for 10k+ orders
      if (queueSize >= BATCH_CONFIG.MASSIVE_BATCH_SIZE) {
        // Massive batch mode - process immediately
        maxBatchSize = BATCH_CONFIG.MASSIVE_BATCH_SIZE;
        delay = BATCH_CONFIG.MASSIVE_BATCH_DELAY_MS;
      } else if (highFrequencyMode.current) {
        // High-frequency mode - larger, faster batches
        maxBatchSize = BATCH_CONFIG.HIGH_FREQUENCY_BATCH_SIZE;
        delay = BATCH_CONFIG.HIGH_FREQUENCY_DELAY_MS;
      } else if (queueSize >= BATCH_CONFIG.LARGE_BATCH_SIZE) {
        // Large batch mode
        maxBatchSize = BATCH_CONFIG.LARGE_BATCH_SIZE;
        delay = BATCH_CONFIG.LARGE_BATCH_DELAY_MS;
      } else if (queueSize === 1 && !highFrequencyMode.current) {
        // Single order mode - process IMMEDIATELY for real-time updates (no delay)
        console.log("⚡ Single order - processing immediately");
        processBatchedOrderUpdates();
        return; // Don't set any timeout
      } else {
        // Normal mode
        maxBatchSize = BATCH_CONFIG.MAX_BATCH_SIZE;
        delay = BATCH_CONFIG.BATCH_DELAY_MS;
      }

      // Process immediately if queue reaches threshold
      if (queueSize >= maxBatchSize) {
        processBatchedOrderUpdates();
      } else {
        batchProcessTimer.current = setTimeout(
          processBatchedOrderUpdates,
          delay
        );
      }
    },
    [processBatchedOrderUpdates]
  );

  // Refresh all refetchable endpoints (orders + account data)
  const refreshAll = async () => {
    try {
      console.log("🔄 Manual refresh requested");
      await Promise.all([
        fetchOrders(),
        fetchUserMappings(), // This now handles both userMappings and indexMultipliers
        fetchRejectedOrders(),
        fetchUsers(),
      ]);
      console.log("✅ Manual refresh completed");
    } catch (error) {
      console.error("❌ refreshAll error:", error);
    }
  };





  useEffect(() => {
    const wsManager = WebSocketManager.getInstance();

    // connect websocket
    wsManager.connect();

    // attach handler for incoming messages
    wsManager.setMessageHandler((raw) => {
      try {
        const data = typeof raw === "string" ? JSON.parse(raw) : raw;
        handleWebSocketMessage(data);
      } catch (err) {
        console.error("❌ WS parse error:", err);
      }
    });

    // attach handlers for state
    wsManager.setOpenHandler(() => {
      console.log("✅ [WS] CONNECTED");
      setLoading((prev) => ({ ...prev, positions: false }));
      setErrors((prev) => ({ ...prev, positions: null }));
      setWsConnected(true);
    });

    wsManager.setErrorHandler((err) => {
      console.error("❌ [WS] ERROR:", err);
      setErrors((prev) => ({ ...prev, positions: "WebSocket error" }));
      setWsConnected(false);
    });

    wsManager.setCloseHandler(() => {
      console.log("🔌 [WS] DISCONNECTED");
      setWsConnected(false);
    });

    return () => {
      wsManager.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------
  // MANUAL ACTIONS USING MANAGER
  // ------------------------------

  const reconnectWebSocket = () => {
    console.log("🔄 Manual reconnect requested");
    WebSocketManager.getInstance().reconnectWebSocket();
  };

  const pingWebSocket = () => {
    console.log("📡 Manual ping");
    WebSocketManager.getInstance().pingWebSocket();
  };

  // Handle WebSocket messages - YOUR FORMAT
  const handleWebSocketMessage = useCallback(
    (data) => {
      const messageType = data.type?.toLowerCase();


    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      calculatePositionPnL,
      shouldShowOrder,
      queueOrderUpdate,
      queuePositionUpdate,
      queueClosedPosition,
      updatePositions,
      updateOrders,
    ]
  );
  
  
  
  
  
  
  // Provide context value
  const value = {
    positions,
    positionsRef, // Direct access to positions ref for optimized components
    positionsUpdateVersion, // Expose version for force re-renders (increments on each update)
    orders,
    ordersRef, // Direct access to orders ref for optimized components
    ordersUpdateVersion, // Expose version for force re-renders (increments on each update)
    userMappings,
    users,
    indexMultipliers, // Transformed format for comparison modals
    indexMultipliersRaw, // Raw format for AccountView
    currentLTPs,
    loading,
    errors,
    rejectedOrders,
    refetch: {
      orders: fetchOrders,
      userMappings: fetchUserMappings, // Now handles both userMappings and indexMultipliers
      users: fetchUsers,
      rejectedOrders: fetchRejectedOrders,
      indexMultipliers: fetchUserMappings, // Point to unified function
    },
  };

  // expose websocket helpers and status
  value.wsConnected = wsConnected;
  value.reconnectWebSocket = reconnectWebSocket;
  value.pingWebSocket = pingWebSocket;
  value.refreshAll = refreshAll;
  value.debugCurrentOrders = debugCurrentOrders; // Debug function
  value.debugCurrentPositions = debugCurrentPositions; // Debug function for positions

  // Add debug info for troubleshooting real-time updates
  value.debug = {
    positionsCount: positions.length,
    ordersCount: orders.length,
    wsConnected,
    lastUpdate: new Date().toISOString(),
  };

  // console.log("🔄 Context value updated:", {
  //   positions: positions.length,
  //   orders: orders.length,
  //   wsConnected,
  //   timestamp: new Date().toLocaleTimeString(),
  // });

  // Global debugging helpers (accessible from browser console)
  useEffect(() => {
    window.debugPositions = debugCurrentPositions;
    window.debugOrders = debugCurrentOrders;
    window.reconnectWS = reconnectWebSocket;
    // console.log(
    //   "🔧 [DEBUG] Global helpers available: window.debugPositions(), window.debugOrders(), window.reconnectWS()"
    // );
  }, [debugCurrentPositions, debugCurrentOrders, reconnectWebSocket]);

  return (
    <GlobalDataContext.Provider value={value}>
      {children}
    </GlobalDataContext.Provider>
  );
}
